import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Lightbox from "@/components/Lightbox";

export default function Photography() {
  const [categories, setCategories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lb, setLb] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/portfolio").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const visible = filter === "all" ? photos : photos.filter((p) => p.categories.includes(filter));

  return (
    <main data-testid="photography-page" className="pt-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-4">Photography</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[1.02] max-w-3xl">
            The work, in its <span className="italic">own light.</span>
          </h1>
          <p className="mt-6 text-sm text-[#4A4A4A] max-w-md leading-relaxed">
            Choose a session below to view its gallery, or browse the full portfolio.
          </p>
        </Reveal>

        {/* Bento categories */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-20">
          {categories.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08} className={i % 5 === 0 ? "sm:col-span-2" : ""}>
              <Link to={`/photography/${c.slug}`} data-testid={`category-card-${c.slug}`} className="group block relative overflow-hidden">
                <img src={imgUrl(c.cover_image)} alt={c.name} loading="lazy"
                  className={`w-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out ${i % 5 === 0 ? "aspect-[16/8]" : "aspect-[4/5]"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/55 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                  <div>
                    <p className="font-serif text-2xl text-[#F9F9F8]">{c.name}</p>
                    {c.starting_price && <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F9F8]/70 mt-2">{c.starting_price}</p>}
                  </div>
                  <ArrowUpRight size={18} strokeWidth={1.25} className="text-[#F9F9F8] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-28"><Marquee items={categories.map((c) => c.name)} /></div>

      {/* Portfolio with filters */}
      <section id="portfolio" data-testid="portfolio-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Portfolio</h2>
          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            <button onClick={() => setFilter("all")} data-testid="filter-all"
              className={`text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${filter === "all" ? "bg-[#1A1A1A] text-[#F9F9F8] border-[#1A1A1A]" : "border-[#1A1A1A]/15 text-[#4A4A4A] hover:border-[#1A1A1A]"}`}>
              All
            </button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setFilter(c.slug)} data-testid={`filter-${c.slug}`}
                className={`text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${filter === c.slug ? "bg-[#1A1A1A] text-[#F9F9F8] border-[#1A1A1A]" : "border-[#1A1A1A]/15 text-[#4A4A4A] hover:border-[#1A1A1A]"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="masonry columns-1 sm:columns-2 lg:columns-3" data-testid="portfolio-grid">
          {visible.map((p, i) => (
            <button key={p.id} onClick={() => setLb(i)} data-testid={`portfolio-photo-${p.id}`}
              className="block w-full overflow-hidden bg-[#EFEFEC] cursor-zoom-in text-left">
              <img src={imgUrl(p.url)} alt={p.caption || "Portfolio photograph"} loading="lazy"
                className="w-full h-auto hover:scale-[1.02] transition-transform duration-[1200ms] ease-out" />
            </button>
          ))}
          {visible.length === 0 && (
            <p className="text-sm text-[#7A7A7A]">No photographs in this category yet.</p>
          )}
        </div>
        <Lightbox photos={visible} index={lb} onClose={() => setLb(null)} onNavigate={setLb} />
      </section>
    </main>
  );
}
